/**
 * 필지 렌더 — 월드맵 위 축소 재현 + 필지 내부 씬(건설용 셀 그리드).
 * 프로토타입 buildCellMesh / rebuildParcelWorldMesh / rebuildParcelInterior 이식.
 */
import * as THREE from 'three';
import { WORLD, type BuildId, type ParcelType } from '@villager/shared';
import { colX, heightAt, rowZ, type World } from '@villager/sim-core';

const { IN, TILE } = WORLD;

const lam = (c: number): THREE.MeshLambertMaterial => new THREE.MeshLambertMaterial({ color: c });

export function buildCellMesh(type: ParcelType, b: BuildId): THREE.Group {
  const g = new THREE.Group();
  if (type === 'village') {
    if (b === 'smith') {
      const x = new THREE.Mesh(new THREE.BoxGeometry(2.6, 1.8, 2.2), lam(0x8a6a4a));
      x.position.y = 0.9;
      g.add(x);
      const r = new THREE.Mesh(new THREE.ConeGeometry(2, 1.4, 4), lam(0x5a4a3a));
      r.position.y = 2.5;
      r.rotation.y = Math.PI / 4;
      g.add(r);
    } else if (b === 'farm') {
      const x = new THREE.Mesh(new THREE.BoxGeometry(3, 0.35, 3), lam(0x8a7444));
      x.position.y = 0.2;
      g.add(x);
      for (let i = -1; i <= 1; i++) {
        const rr = new THREE.Mesh(new THREE.BoxGeometry(2.7, 0.3, 0.5), lam(0x7a9a44));
        rr.position.set(0, 0.5, i * 0.95);
        g.add(rr);
      }
    } else if (b === 'inn') {
      const x = new THREE.Mesh(new THREE.BoxGeometry(2.8, 2.2, 2.4), lam(0xcbb794));
      x.position.y = 1.1;
      g.add(x);
      const r = new THREE.Mesh(new THREE.ConeGeometry(2.2, 1.5, 4), lam(0x9a4a34));
      r.position.y = 3;
      r.rotation.y = Math.PI / 4;
      g.add(r);
    } else if (b === 'tower') {
      const x = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 1, 4.6, 6), lam(0x7a7264));
      x.position.y = 2.3;
      g.add(x);
      const r = new THREE.Mesh(new THREE.ConeGeometry(1.2, 1.3, 6), lam(0x9a4a34));
      r.position.y = 5.2;
      g.add(r);
    } else if (b === 'deco') {
      const colors = [0xd88ab0, 0xe8b34b, 0xb07ad8];
      for (let i = 0; i < 3; i++) {
        const f = new THREE.Mesh(new THREE.SphereGeometry(0.35, 6, 5), lam(colors[i]!));
        f.position.set((Math.random() - 0.5) * 1.6, 0.4, (Math.random() - 0.5) * 1.6);
        g.add(f);
      }
    }
  } else {
    if (b === 'den') {
      const x = new THREE.Mesh(new THREE.DodecahedronGeometry(1.4, 0), lam(0x5a5a6c));
      x.position.y = 0.8;
      g.add(x);
      const h = new THREE.Mesh(
        new THREE.CircleGeometry(0.55, 8),
        new THREE.MeshBasicMaterial({ color: 0x0e0c0a }),
      );
      h.position.set(0, 0.6, 1.35);
      g.add(h);
    } else if (b === 'chest') {
      const x = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.8, 0.8), lam(0x8a6a34));
      x.position.y = 0.4;
      g.add(x);
      const l = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.25, 0.8), lam(0xd9a441));
      l.position.y = 0.9;
      g.add(l);
    } else if (b === 'torch') {
      const x = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.12, 1.6, 4), lam(0x4a3a2a));
      x.position.y = 0.8;
      g.add(x);
      const f = new THREE.Mesh(
        new THREE.SphereGeometry(0.3, 6, 5),
        new THREE.MeshBasicMaterial({ color: 0xff9a3a }),
      );
      f.position.y = 1.7;
      g.add(f);
    }
  }
  return g;
}

/** 월드맵 위 필지 축소 메시 재구성. meshMap이 이전 메시를 추적. */
export function rebuildParcelWorldMesh(
  worldGroup: THREE.Group,
  w: World,
  c: number,
  r: number,
  meshMap: Map<string, THREE.Group>,
): void {
  const key = `${c},${r}`;
  const old = meshMap.get(key);
  if (old) {
    worldGroup.remove(old);
    meshMap.delete(key);
  }
  const p = w.grid[c]?.[r]?.parcel;
  if (!p) return;
  const g = new THREE.Group();
  const bx = colX(c);
  const bz = rowZ(r);
  const by = heightAt(bx, bz);
  const plate = new THREE.Mesh(
    new THREE.BoxGeometry(TILE * 0.92, 0.5, TILE * 0.92),
    lam(p.type === 'village' ? 0x5d7448 : 0x3e3648),
  );
  plate.position.y = 0.25;
  g.add(plate);
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(TILE * 0.46, 0.25, 4, 4),
    lam(p.paralyzed > 0 ? 0xc04a2e : 0xd9a441),
  );
  rim.rotation.x = Math.PI / 2;
  rim.rotation.z = Math.PI / 4;
  rim.position.y = 0.5;
  g.add(rim);
  for (let i = 0; i < IN; i++) {
    for (let j = 0; j < IN; j++) {
      const cell = p.cells[i]![j]!;
      if (!cell.b) continue;
      const m = buildCellMesh(p.type, cell.b);
      m.scale.setScalar(0.85);
      m.position.set((i - (IN - 1) / 2) * TILE * 0.17, 0.5, (j - (IN - 1) / 2) * TILE * 0.17);
      g.add(m);
    }
  }
  g.position.set(bx, by, bz);
  worldGroup.add(g);
  meshMap.set(key, g);
}

/** 필지 내부 씬 구성. 클릭 대상 셀 타일 메시 배열 반환(userData={i,j}). */
export function buildParcelInterior(parcelScene: THREE.Group, w: World, c: number, r: number): THREE.Mesh[] {
  parcelScene.clear();
  const cellMeshes: THREE.Mesh[] = [];
  const p = w.grid[c]![r]!.parcel!;
  const CS = 9;
  const floor = new THREE.Mesh(
    new THREE.BoxGeometry(IN * CS + 4, 1, IN * CS + 4),
    lam(p.type === 'village' ? 0x55694a : 0x38313f),
  );
  floor.position.y = -0.5;
  parcelScene.add(floor);
  for (let i = 0; i < IN; i++) {
    for (let j = 0; j < IN; j++) {
      const cell = p.cells[i]![j]!;
      const even = (i + j) % 2 === 0;
      const color = even
        ? p.type === 'village'
          ? 0x5a7044
          : 0x3c3446
        : p.type === 'village'
          ? 0x617548
          : 0x423a4c;
      const tile = new THREE.Mesh(new THREE.BoxGeometry(CS * 0.92, 0.5, CS * 0.92), lam(color));
      tile.position.set((i - (IN - 1) / 2) * CS, 0.25, (j - (IN - 1) / 2) * CS);
      tile.userData = { i, j };
      parcelScene.add(tile);
      cellMeshes.push(tile);
      if (cell.b) {
        const m = buildCellMesh(p.type, cell.b);
        m.scale.setScalar(2.2);
        m.position.set(tile.position.x, 0.5, tile.position.z);
        parcelScene.add(m);
      }
    }
  }
  return cellMeshes;
}
